import { motion } from 'motion/react';
import LogoMark from '@/src/components/LogoMark';

// One loading screen for the whole start-up (auth check, profile check, loading the venue),
// so signing in shows a single screen instead of three different ones in a row.
// Works outside the ThemeProvider: reads the saved theme directly.
let animatedOnce = false;

function isDark(): boolean {
  try {
    const root = document.documentElement;
    if (root.classList.contains('light')) return false;
    if (root.classList.contains('dark')) return true;
    const t = localStorage.getItem('opsalo-theme');
    if (t === 'light') return false;
    if (t === 'dark') return true;
    return !window.matchMedia?.('(prefers-color-scheme: light)').matches;
  } catch { return true; }
}

export default function BootScreen({ label = 'Loading', children }: { label?: string; children?: React.ReactNode }) {
  const dark = isDark();
  const bg = dark ? '#0F0F0D' : '#FAF8F9';
  const text = dark ? '#F0EEE8' : '#1D1D18';
  const faint = dark ? 'rgba(255,255,255,0.25)' : '#A6A69A';
  const track = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const first = !animatedOnce;
  animatedOnce = true;
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center z-[100]" style={{ background: bg }}>
      <motion.div
        initial={first ? { scale: 0.8, opacity: 0 } : false}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className="flex flex-col items-center gap-5"
      >
        <LogoMark size={56} color={text} />
        <div className="flex flex-col items-center gap-1.5">
          <p className="text-lg font-medium" style={{ color: text }}>OpsAlo</p>
          <p className="text-[11px] tracking-widest uppercase" style={{ color: faint }}>{label}</p>
        </div>
        <div className="w-20 h-0.5 rounded-full overflow-hidden" style={{ background: track }}>
          <motion.div initial={{ x: '-100%' }} animate={{ x: '100%' }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-full h-full rounded-full" style={{ background: '#4A9A4A' }} />
        </div>
      </motion.div>
      {children}
    </div>
  );
}
